import React, { useContext, useState } from 'react';
import { motion } from 'framer-motion';
import { FaWhatsapp } from 'react-icons/fa';
import { LanguageContext } from '../../../contexts/LanguageContext';
import { RESTAURANT_WHATSAPP_URL } from '../../../constants/contact';
import { openReservationModal } from '../../../utils/openReservationModal';
import '../../Header/css/Header.css';
import logo from '../../../assets/images/Logo.png';

const navItemVariants = {
  hidden: { opacity: 0, y: -20 },
  visible: { opacity: 1, y: 0 },
};

const logoVariants = {
  hidden: { opacity: 0, scale: 0.8 },
  visible: { opacity: 1, scale: 1 },
};

const mobileMenuVariants = {
  hidden: { opacity: 0, y: -20 },
  visible: { opacity: 1, y: 0 },
};

const navGroups = {
  left: ['home', 'about'],
  right: ['menu', 'location'],
};

const languages = ['en', 'es', 'ca'];

const Header = () => {
  const { lang, t, setLang } = useContext(LanguageContext);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen((open) => !open);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const handleReserveClick = () => {
    closeMobileMenu();
    openReservationModal();
  };

  return (
    <header className="header-container">
      <div className="header-inner">
        <div className="header-side header-side-left">
          <motion.button
            type="button"
            className="mobile-menu-icon"
            onClick={toggleMobileMenu}
            aria-label="Toggle navigation menu"
            aria-controls="mobile-menu-panel"
            aria-expanded={isMobileMenuOpen}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: false, amount: 0.5 }}
            variants={{ hidden: { opacity: 0, x: -30 }, visible: { opacity: 1, x: 0 } }}
            transition={{ duration: 0.5 }}
          >
            <span />
            <span />
            <span />
          </motion.button>

          <a
            className="header-whatsapp-link"
            href={RESTAURANT_WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            <FaWhatsapp aria-hidden="true" />
            <span>{t('whatsapp')}</span>
          </a>

          <nav className="nav-left" aria-label="Primary">
            {navGroups.left.map((item, index) => (
              <motion.a
                key={item}
                href={`#${item}`}
                onClick={closeMobileMenu}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: false, amount: 0.5 }}
                variants={navItemVariants}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ scale: 1.04, y: -2 }}
              >
                {t(item)}
              </motion.a>
            ))}
          </nav>
        </div>

        <motion.a
          href="#home"
          className="logo-center"
          aria-label="Go to home"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false, amount: 0.5 }}
          variants={logoVariants}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <img src={logo} alt="Vatti Taverna logo" />
        </motion.a>

        <div className="header-side header-side-right">
          <nav className="nav-right" aria-label="Sections">
            {navGroups.right.map((item, index) => (
              <motion.a
                key={item}
                href={`#${item}`}
                onClick={closeMobileMenu}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: false, amount: 0.5 }}
                variants={navItemVariants}
                transition={{ duration: 0.5, delay: 0.2 + index * 0.1 }}
                whileHover={{ scale: 1.04, y: -2 }}
              >
                {t(item)}
              </motion.a>
            ))}
          </nav>

          <motion.div
            className="language-switcher"
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: false, amount: 0.5 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            {languages.map((code) => (
              <button
                key={code}
                type="button"
                className={lang === code ? 'active' : ''}
                onClick={() => setLang(code)}
                aria-label={`Switch language to ${code.toUpperCase()}`}
              >
                {code.toUpperCase()}
              </button>
            ))}
          </motion.div>
        </div>
      </div>

      {isMobileMenuOpen && (
        <motion.div
          id="mobile-menu-panel"
          className="mobile-menu"
          initial="hidden"
          whileInView="visible"
          exit="hidden"
          variants={mobileMenuVariants}
          viewport={{ once: false, amount: 0.5 }}
          transition={{ duration: 0.35 }}
        >
          {['home', 'about', 'menu', 'location'].map((item, index) => (
            <motion.a
              key={item}
              href={`#${item}`}
              onClick={closeMobileMenu}
              whileHover={{ scale: 1.03, x: 3 }}
              transition={{ duration: 0.2, delay: index * 0.08 }}
            >
              {t(item)}
            </motion.a>
          ))}

          <button type="button" className="mobile-menu-button primary" onClick={handleReserveClick}>
            {t('reserve')}
          </button>

          <a
            className="mobile-menu-button secondary"
            href={RESTAURANT_WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            <FaWhatsapp aria-hidden="true" />
            <span>{t('heroWhatsapp')}</span>
          </a>

          <div className="mobile-language-switcher">
            {languages.map((code) => (
              <button
                key={code}
                type="button"
                className={lang === code ? 'active' : ''}
                onClick={() => setLang(code)}
              >
                {code.toUpperCase()}
              </button>
            ))}
          </div>
        </motion.div>
      )}
    </header>
  );
};

export default Header;
